// Curated "Been there, done that" list — a tasteful subset of the
// Amsterdam first-visit canon. Inspired by amsterdamian.com's
// "Things Not to Miss in Amsterdam" piece (we credit them in the
// screen footer) plus our own picks. Not a copy: descriptions are
// our own and several items aren't in the source piece at all.

export interface ChecklistItem {
  id: string;
  title: string;
  description: string;
  emoji: string;
  category: ChecklistCategory;
  /** If the item is directly inspired by the source, mark it so the UI
   *  shows the "as seen on amsterdamian.com" credit appropriately. */
  fromSource?: boolean;
}

export type ChecklistCategory = "icons" | "food" | "neighborhoods" | "hidden" | "seasonal";

export const CATEGORY_LABEL: Record<ChecklistCategory, string> = {
  icons:         "Cultural icons",
  food:          "Food + drink",
  neighborhoods: "Neighborhoods",
  hidden:        "Hidden corners",
  seasonal:      "Seasonal",
};

export const CHECKLIST: ChecklistItem[] = [
  // ── Cultural icons ────────────────────────────────────────────────
  { id: "canal-cruise",    category: "icons", emoji: "🚤",
    title: "Take a canal cruise",
    description: "Pick a small open boat over the big-window glass barges. Sunset slot if you can swing it.",
    fromSource: true },
  { id: "anne-frank",      category: "icons", emoji: "📔",
    title: "Visit the Anne Frank House",
    description: "Book the ticket weeks ahead. Walk through the hidden annex behind the bookcase.",
    fromSource: true },
  { id: "rijks-vangogh",   category: "icons", emoji: "🖼️",
    title: "Pick a Museumplein museum",
    description: "Rijksmuseum, Van Gogh, or Stedelijk. Doing all three in a day burns out fast — choose one.",
    fromSource: true },
  { id: "live-music",      category: "icons", emoji: "🎶",
    title: "Catch a show at Paradiso, Melkweg, or Concertgebouw",
    description: "Old church → club, sugar factory → venue, or marble concert hall. All three count.",
    fromSource: true },
  { id: "bloemenmarkt",    category: "icons", emoji: "🌷",
    title: "Drift through the Bloemenmarkt",
    description: "Floating flower market on the Singel. Tourist-trap meets tulip-bulb shopping; do both knowingly.",
    fromSource: true },
  { id: "royal-palace",    category: "icons", emoji: "👑",
    title: "Stand in Dam Square",
    description: "The Royal Palace was a 17th-century town hall. Snack a stroopwafel on the steps and watch the pigeons get bullied.",
    fromSource: true },

  // ── Food + drink ──────────────────────────────────────────────────
  { id: "raw-herring",     category: "food", emoji: "🐟",
    title: "Eat raw herring like a local",
    description: "Onion + pickles, tilt your head back, lower it in by the tail. Fish stalls do it best.",
    fromSource: true },
  { id: "stroopwafel",     category: "food", emoji: "🍪",
    title: "Eat a fresh-pressed stroopwafel",
    description: "Off the iron, syrup still warm. Albert Cuypmarkt or Noordermarkt — supermarket ones don't count.",
    fromSource: true },
  { id: "bitterballen",    category: "food", emoji: "🟤",
    title: "Order bitterballen with mustard",
    description: "Order with a small beer. Wait three minutes longer than you want to — the inside is lava.",
    fromSource: true },
  { id: "rijsttafel",      category: "food", emoji: "🍛",
    title: "Share an Indonesian rijsttafel",
    description: "Fifteen-plus small plates — sambal, rendang, satay. Bring four friends and order the works.",
    fromSource: true },
  { id: "brown-cafe",      category: "food", emoji: "🍺",
    title: "Sink an afternoon at a brown café",
    description: "Café Hoppe, Café Chris, Café 't Smalle. Wooden everything, sand on the floor.",
    fromSource: true },
  { id: "dutch-cheese",    category: "food", emoji: "🧀",
    title: "Taste old + young Dutch cheese side by side",
    description: "A real cheesemonger, not the tourist stalls. Ask for jong, belegen, and oud, in that order.",
    fromSource: true },
  { id: "albert-cuyp",     category: "food", emoji: "🥬",
    title: "Wander Albert Cuypmarkt",
    description: "Half kilometre of stalls in De Pijp. Better in the morning before the tourists wake up.",
    fromSource: true },
  { id: "jenever",         category: "food", emoji: "🥃",
    title: "Drink jenever the proper way",
    description: "Hands behind your back, lean in, sip the meniscus off the top of the tulip glass. Not a copy of the source — this one is local-only.",
    fromSource: false },

  // ── Neighborhoods ─────────────────────────────────────────────────
  { id: "jordaan",         category: "neighborhoods", emoji: "🚲",
    title: "Lose yourself in the Jordaan",
    description: "Narrow streets, hofjes tucked behind façades, the most photogenic working-class-turned-trendy quarter.",
    fromSource: true },
  { id: "noord-ferry",     category: "neighborhoods", emoji: "⛴️",
    title: "Take the free ferry to Noord",
    description: "EYE Filmmuseum, NDSM yards, the A'DAM Lookout swing. Departs every few minutes behind Centraal — and it's free.",
    fromSource: true },
  { id: "de-pijp",         category: "neighborhoods", emoji: "🏘️",
    title: "Spend an afternoon in De Pijp",
    description: "South of the Singelgracht. Albert Cuyp + Sarphatipark + a hundred small restaurants packed into ten streets.",
    fromSource: true },
  { id: "nine-streets",    category: "neighborhoods", emoji: "🛍️",
    title: "Shop the Nine Streets (De Negen Straatjes)",
    description: "Tiny canal-side blocks linking Prinsengracht and Singel. Vintage, tiny galleries, single-origin coffee.",
    fromSource: true },
  { id: "vondelpark",      category: "neighborhoods", emoji: "🌳",
    title: "Picnic in Vondelpark",
    description: "Pick a Sunday afternoon. Free outdoor theatre in summer; everyone-and-their-dog year round.",
    fromSource: true },

  // ── Hidden corners ────────────────────────────────────────────────
  { id: "begijnhof",       category: "hidden", emoji: "🕊️",
    title: "Slip into the Begijnhof",
    description: "14th-century courtyard hidden one door off Spui. Keep your voice down — people live there.",
    fromSource: true },
  { id: "magere-brug",     category: "hidden", emoji: "🌉",
    title: "Cross the Magere Brug at dusk",
    description: "The skinny wooden drawbridge on the Amstel. 1,200 little bulbs come on at twilight.",
    fromSource: true },
  { id: "hofje",           category: "hidden", emoji: "🏡",
    title: "Find a hofje you weren't looking for",
    description: "Karthuizerhof, Suykerhoffje, Begijnhof. Tiny almshouse courtyards behind anonymous gates.",
    fromSource: true },
  { id: "cycle-canal",     category: "hidden", emoji: "🚴",
    title: "Cycle a canal at midnight",
    description: "Pick a quiet weeknight. Hire a bike with the seat too high; commit to the bell. Not in the source — but unmissable.",
    fromSource: false },
  { id: "windmill-beer",   category: "hidden", emoji: "🌾",
    title: "Pint under the windmill at Brouwerij 't IJ",
    description: "Molen De Gooyer + an unfiltered IPA. Easternmost windmill in the centre.",
    fromSource: true },

  // ── Seasonal ──────────────────────────────────────────────────────
  { id: "kingsday",        category: "seasonal", emoji: "🟠",
    title: "Survive King's Day (27 April)",
    description: "City turns orange. Boats on every canal. Vrijmarkt = neighbours selling junk on the pavement.",
    fromSource: true },
  { id: "light-festival",  category: "seasonal", emoji: "💡",
    title: "Walk the Light Festival route (Nov–Jan)",
    description: "Outdoor light-art installations along the canals. Do it on a freezing weeknight, not a Saturday.",
    fromSource: true },
];

export const TOTAL = CHECKLIST.length;
